import { filterClasses } from "@swivl-mono/helpers";
import { TicketPreview, formatTicketNumberDisplay, ticketPriorityLabels } from "@swivl-mono/types";
import { DataTable, DataTableVariant } from "@swivl-mono/ui";
import { ColumnDef } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { FiChevronRight } from "react-icons/fi";

import { statusConfig } from "./TicketListItem";

interface TicketSearchResultsProps {
  rows: TicketPreview[];
  loading: boolean;
  selectedId: number | null;
  /** memberId → display name, for the Assignee column. */
  memberMap: Map<number, string>;
  /** group slug → name, for the Group column. */
  groupMap: Map<string, string>;
  /** locationId → name, for the Location column. */
  locationMap: Map<number, string>;
  onRowClick: (ticketId: number) => void;
}

/** Static (non-editable) status pill matching the inbox list badge colors. */
function StatusPill({ status }: { status: TicketPreview["status"] }): React.JSX.Element {
  const cfg = statusConfig[status];
  return (
    <span className={filterClasses("body-xs inline-flex h-[18px] items-center justify-center rounded-sm px-1", cfg.bg, cfg.text)}>{cfg.label}</span>
  );
}

/** Results table for the Search All page — one row per matching ticket. */
export function TicketSearchResults({
  rows,
  loading,
  selectedId,
  memberMap,
  groupMap,
  locationMap,
  onRowClick
}: TicketSearchResultsProps): React.JSX.Element {
  const columns = useMemo<ColumnDef<TicketPreview, unknown>[]>(() => {
    const cols: ColumnDef<TicketPreview, unknown>[] = [
      {
        id: "title",
        header: "Ticket Title",
        accessorKey: "title",
        cell: ({ row }) => {
          const t = row.original;
          return (
            <div className="flex min-w-0 items-center gap-1 overflow-hidden">
              <span className="body-sm truncate text-black">{t.title}</span>
              <span className="heading-mono shrink-0 text-grey-600">#{formatTicketNumberDisplay(t.number)}</span>
            </div>
          );
        }
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => <StatusPill status={row.original.status} />
      },
      {
        id: "priority",
        header: "Priority",
        accessorKey: "priority",
        cell: ({ row }) => <span className="body-sm text-black">{ticketPriorityLabels[row.original.priority]}</span>
      },
      {
        id: "assignee",
        header: "Assignee",
        cell: ({ row }) => {
          const names = row.original.assigneeMemberIds.map((id) => memberMap.get(id)).filter(Boolean);
          return <span className="body-sm text-black">{names.length ? names.join(", ") : "–"}</span>;
        }
      },
      {
        id: "group",
        header: "Group",
        cell: ({ row }) => {
          const slug = row.original.routingGroupSlug;
          return <span className="body-sm text-black">{(slug && groupMap.get(slug)) || "–"}</span>;
        }
      },
      {
        id: "location",
        header: "Location",
        cell: ({ row }) => {
          const id = row.original.locationId;
          return <span className="body-sm text-black">{(id != null && locationMap.get(id)) || "–"}</span>;
        }
      },
      {
        id: "chevron",
        header: "",
        // Fixed narrow width so the freed space goes to the content columns.
        size: 48,
        minSize: 48,
        maxSize: 48,
        cell: () => <FiChevronRight className="h-4 w-4 text-grey-600" />
      }
    ];
    // No sorting in the search results header.
    return cols.map((c) => ({ ...c, enableSorting: false }));
  }, [memberMap, groupMap, locationMap]);

  return (
    <DataTable
      columns={columns}
      data={rows}
      variant={DataTableVariant.SECONDARY}
      areRowsSelectable
      handleSelectCell={(id) => onRowClick(Number(id))}
      selectedRowId={selectedId != null ? String(selectedId) : null}
      hasCheckboxColumn={false}
      hasFooter={false}
      loading={loading}
    />
  );
}
